import { useEffect, useRef, useState } from 'react';
import type { PickedContact } from '@/lib/apiClient';
import { contactLine, type JobContactOption } from './jobContacts';

/**
 * Who an RFI is assigned to, or who reviews a submittal (#598 step 2): a person
 * from the job's Contacts tab, or — as before — a role label, or anything typed
 * under "Other…". Contacts come first: with them the email draft is addressed
 * for you and the send records who it went to.
 *
 * The value is the label shown (`label`) plus the contact it names, if any. A
 * contact no longer on the job still shows as the current value, by the name
 * stored, rather than vanishing from the field.
 */

export interface PartyValue {
  contactId: number | null;
  /** What "Assigned to" / "Reviewer" reads: the contact's name, a role, or typed text. */
  label: string;
}

const css = `
  .jcp-field { display: flex; flex-direction: column; }
  .jcp-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); margin-bottom: 5px; }
  .jcp-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); width: 100%;
    background: #fff; color: var(--ew-dark);
  }
  .jcp-hint { margin-top: 4px; color: var(--ew-n-500); font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); }
`;

// The same values SelectWithOther uses for a role and for Other…, so the picker
// reads the same to anything that drives it; only a job contact is marked (c:12).
const OTHER = 'Other…';
const asContact = (id: number) => `c:${id}`;
const asRole = (label: string) => label;

export default function ContactOrRoleSelect({
  label, contacts, roles, value, onChange, placeholder, current, onPendingChange, loading = false,
}: {
  label?: string;
  contacts: JobContactOption[];
  roles: string[];
  value: PartyValue;
  onChange: (v: PartyValue) => void;
  placeholder?: string;
  /** The contact the record names now, so one taken off the job still shows. */
  current?: PickedContact | null;
  /** True while "Other…" is chosen and nothing typed: see SelectWithOther. */
  onPendingChange?: (pending: boolean) => void;
  /**
   * The job's contacts are still being fetched. The current contact then shows
   * by name, not as "no longer on this job", which it would read as for the
   * moment before the list arrives (review).
   */
  loading?: boolean;
}) {
  const isTyped = value.contactId == null && value.label !== '' && !roles.includes(value.label);
  const [otherMode, setOtherMode] = useState(isTyped);
  const showOther = value.contactId == null && (otherMode || isTyped);
  const pending = showOther && value.label === '';
  const report = useRef(onPendingChange);
  report.current = onPendingChange;
  useEffect(() => { report.current?.(pending); }, [pending]);
  useEffect(() => () => report.current?.(false), []);

  const onJob = value.contactId == null || contacts.some((c) => c.contactId === value.contactId);
  const selected = value.contactId != null ? asContact(value.contactId)
    : showOther ? OTHER
      : value.label ? asRole(value.label) : '';

  return (
    <div className="jcp-field">
      <style>{css}</style>
      {label ? <label className="jcp-label">{label}</label> : null}
      <select
        className="jcp-input"
        aria-label={label}
        value={selected}
        onChange={(e) => {
          const v = e.target.value;
          if (v === OTHER) { setOtherMode(true); onChange({ contactId: null, label: '' }); return; }
          setOtherMode(false);
          if (v.startsWith('c:')) {
            const id = Number(v.slice(2));
            const c = contacts.find((x) => x.contactId === id);
            onChange({ contactId: id, label: c?.name ?? value.label });
          } else {
            onChange({ contactId: null, label: v });
          }
        }}
      >
        <option value="">{placeholder ?? 'Select…'}</option>
        {(contacts.length > 0 || !onJob) && (
          <optgroup label="On this job">
            {contacts.map((c) => (
              <option key={c.contactId} value={asContact(c.contactId)}>{contactLine(c)} — {c.roles}</option>
            ))}
            {!onJob && value.contactId != null && (
              <option value={asContact(value.contactId)}>
                {contactLine({ name: current?.name ?? value.label, companyName: current?.companyName ?? null })}{loading ? '' : ' (no longer on this job)'}
              </option>
            )}
          </optgroup>
        )}
        <optgroup label="A role">
          {roles.map((r) => <option key={r} value={asRole(r)}>{r}</option>)}
        </optgroup>
        <option value={OTHER}>{OTHER}</option>
      </select>
      {showOther && (
        <input
          className="jcp-input"
          style={{ marginTop: 'var(--ew-s-2)' }}
          placeholder="Enter a value…"
          value={value.label}
          onChange={(e) => onChange({ contactId: null, label: e.target.value })}
          autoFocus
        />
      )}
      {contacts.length === 0 && !loading && (
        <span className="jcp-hint">Add the job’s people on its Contacts tab to pick them here.</span>
      )}
    </div>
  );
}
